import { useCallback, useSyncExternalStore } from 'react'

// Whether a retry budget is spent describes an SWR *cache key*, not a component.
// SWR hands `onErrorRetry`/`onSuccess` only to the instance that started the
// request, and dispatches each retry to `EVENT_REVALIDATORS[key][0]`, the
// first-registered subscriber. So a flag held in component state is written by
// whichever instance happens to own the request and stays invisible to every
// other subscriber on the same key: one reads "budget spent" while another waits
// forever on a retry that will never be reported to it.
//
// Keying the state by the SWR key instead makes every subscriber agree. It is a
// store rather than a plain map because clearing it has to move consumers off
// whatever they rendered while it was set, and a module variable read during
// render gives React nothing to subscribe to.

const spentKeys = new Set<string>()
const listeners = new Set<() => void>()

function emit(): void {
  for (const listener of listeners) {
    listener()
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Call when a key's retries are exhausted, so its failure is final. */
export function markRetryBudgetSpent(key: string): void {
  if (spentKeys.has(key)) return
  spentKeys.add(key)
  emit()
}

/** Call when a fresh attempt starts or succeeds, reopening the grace period. */
export function clearRetryBudgetSpent(key: string): void {
  if (!spentKeys.has(key)) return
  spentKeys.delete(key)
  emit()
}

/** True once the key's retries are exhausted. False for a null (unused) key. */
export function useRetryBudgetSpent(key: string | null): boolean {
  const getSnapshot = useCallback(() => key !== null && spentKeys.has(key), [key])
  return useSyncExternalStore(subscribe, getSnapshot, () => false)
}

/** Test-only: drop all state so keys don't leak between cases. */
export function resetRetryBudgets(): void {
  if (spentKeys.size === 0) return
  spentKeys.clear()
  emit()
}
